// Template for the outcome of a scenario
import { compactInteger, toFixed } from 'humanize-plus';
import { get } from 'lodash';
import React from 'react';
import { connect } from 'react-redux'
import { Table } from 'semantic-ui-react'

import { ProjectionType } from '../../proptypes'
import GoalSummary from './goalSummary';

// State
import {
  getPrivateElectrificationVehicleGoal,
  getProjectionYear,
  getPublicCommercialElectrificationVehicleGoal,
} from '../../store/selectors';

// Store Calculated Metrics
import { IAppState } from '../../store/reducers';
import {
  getProjectedGrossDomesticProduct,
} from '../../store/selectors/gdp';
import {
  getProjectedModalSplitByOwnerAndTypeSeries,
  getProjectedModalSplitByOwnershipSeries,
  getProjectedModalSplitByTypeSeries,
  makeGetProjectedModalSplitByOwnerAndTypeSeries,
} from '../../store/selectors/modalSplit';
import {
  getPassengerDistanceTraveled, IChartMapping
} from '../../store/selectors/passengerDistanceTraveled';
import {
  getProjectedPopulation
} from '../../store/selectors/population';
import {
  getVKTTotal,
} from '../../store/selectors/vehicleDistanceTraveled';
import {
  IOwnershipMode
} from '../../store/types/city';

import { getUtilizationSeries } from '../../store/selectors/utilization';

// Carbon emissions!

// Emissions
import {
  getCarbonEmissionsTotal,
} from '../../store/selectors/carbonPollution';
import {
  getPMEmissionsTotal
} from '../../store/selectors/pmPollution';

// Can put this config somewhere...
const ownershipModalSplitDisplayNames = [
  ['Cars / Private', 'car.private'],
  ['Cars / Commercial', 'car.commercial'],
  ['Auto rickshaws / Commercial', 'autoRickshaw.commercial'],
  ['2 wheelers / Private', 'twoWheel.private'],
  ['2 wheelers / Commercial', 'twoWheel.commercial'],
  ['Bus / Public', 'publicTransit.bus'],
  ['Metro / Public', 'publicTransit.metro'],
];


interface IModalCategory {
  name: string;
  value: any; // really a number...
}

// consume IModalSplit
const flattenModalSplit = (modalSplit: IOwnershipMode) : IModalCategory[]=> {
  return ownershipModalSplitDisplayNames.map(([name, path]) => ({
    name,
    value: get(modalSplit, path)
  }))
}

// Eventually: turn a list of proportions into JSX
// Consumes a list of objects with name,value keys
// Return JSX of how to present it
const formatModalSplit = (modalSplit: IModalCategory[]) => {
  return (
    <Table compact={true} size='small'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell collapsing={true}>Vehicle/Ownership</Table.HeaderCell>
          <Table.HeaderCell textAlign='right' collapsing={true}>%</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {modalSplit.map(category => {
          return (
          <Table.Row
            key={category.name}
          >
            <Table.Cell>{category.name}</Table.Cell>
            <Table.Cell textAlign='right' collapsing={true}>
                {toFixed(category.value * 100, 2)}
            </Table.Cell>
          </Table.Row>
          )
        })}
      </Table.Body>
    </Table>
  )
}

interface IProps {
  // values
  projectionYear: number;
  population: number;
  gdp: number;
  goals: object;

  pm25Emissions: number;
  co2Emissions: number;
  vehicleDistanceTraveled: number; // fix this later
  passengerDistanceTraveled: IChartMapping;
  scenarioKey: string;

  // Electric goals
  privateElectricVehiclePercentage: number;
  publicElectricVehiclePercentage: number;

  vehicleStock: any;

  // debugging
  emissionsRate: any; // debugging
  electricDistance: any;
  carbonSeries: any;
  carbonTotal: number;

  modalSplit: IOwnershipMode[];
}

// Assumes all values in the summary will be for the same year
const ScenarioSummary: React.SFC<IProps> = (props) => {
  const { projectionYear, population, gdp } = props; // projection
  // Values that we're projecting out on
  const {
    modalSplit: modalSplitSeries,
    pm25Emissions,
    co2Emissions,
    vehicleDistanceTraveled,
    passengerDistanceTraveled,
    scenarioKey
  } = props;

  const scenarioPDT = passengerDistanceTraveled[scenarioKey];
  const scenarioVKT = vehicleDistanceTraveled;

  const lastSplit = modalSplitSeries.slice(-1)[0];
  const modalSplit = flattenModalSplit(lastSplit);

  return (
    <div>
      <p>
        Based on your input, in {projectionYear}, we project your city's population to be {compactInteger(population,2)} and GDP to be {compactInteger(gdp,2)} (2015 USD).
      </p>
      <p>Your electrification goals for vehicles:</p>
      <ul>
        <li>Privately owned: {props.privateElectricVehiclePercentage}%</li>
        <li>Public and commercial: {props.publicElectricVehiclePercentage}%</li>
      </ul>
      {props.goals && <GoalSummary {...props.goals} scenarioKey={props.scenarioKey}/>}
      <p>Projected outcomes in {projectionYear}:</p>
      <ul>
        <li>
          Passenger Travel: {compactInteger(scenarioPDT * population, 3)} km
        </li>
        <li>
          Vehicle Travel: {toFixed(scenarioVKT, 3)}B km {/* billion */}
        </li>
        <li>
          CO2 Emissions: {toFixed(co2Emissions, 4)} Mtons
        </li>
        <li>
          PM 2.5 Emissions: {toFixed(pm25Emissions, 7)} Mtons
        </li>

      </ul>
      <p>The projected modal split: </p>
      {formatModalSplit(modalSplit)}
  </div>);
}

// Temporarily break proptypes in favor of typescript... something got nullable along the way
// ScenarioSummary.propTypes = {
//   ...ProjectionType,
// }

const makeMapStateToProps = () => {

  const mapStateToProps = (state: IAppState, props: IProps) => ({
    // bpkm: getBPKM(state),
    // bpkmSeries: getBPKMSeries(state),
    gdp: getProjectedGrossDomesticProduct(state),
    modalSplit: getProjectedModalSplitByOwnerAndTypeSeries(state, props),
    passengerDistanceTraveled: getPassengerDistanceTraveled(state),
    population: getProjectedPopulation(state),
    projectionYear: getProjectionYear(state),
    utilization: getUtilizationSeries(state),
    vehicleDistanceTraveled: getVKTTotal(state, props),

    // Scenario Electric Goals
    privateElectricVehiclePercentage: getPrivateElectrificationVehicleGoal(state, props),
    publicElectricVehiclePercentage: getPublicCommercialElectrificationVehicleGoal(state, props),

    // distance traveled
    // carbonSeries: getElectricEmissionsTotal(state, props),
    // carbonSeries: getGasolineEmissionsTotal(state, props),
    // carbonSeries: getCarbonEmissionsSeries(state, props),
    co2Emissions: getCarbonEmissionsTotal(state, props), // overrides old prop
    pm25Emissions: getPMEmissionsTotal(state, props), // overrides old prop...
  })

  return mapStateToProps;
}


export default connect(makeMapStateToProps, null)(ScenarioSummary);



// WEBPACK FOOTER //
// ./src/components/scenarioCard/scenarioSummary.tsx